/* StyleOmg quantity selector and shipping progress: stateful across Shopify section rerenders. */
(() => {
  'use strict';
  if (customElements.get('styleomg-quantity-breaks')) return;

  const remembered = new Map();
  const fallback120 = {
    USD:120,EUR:110,GBP:95,CAD:165,AUD:185,NZD:205,SGD:155,
    HKD:940,JPY:18000,KRW:165000,CHF:105,SEK:1250,NOK:1300,
    DKK:820,PLN:470,CZK:2600,HUF:42000,RON:550,ILS:390,
    MXN:2250,BRL:650,INR:10500,VND:3100000,TWD:3700,
    CNY:860,AED:440,SAR:450,THB:3900,PHP:7000,ZAR:2050,MYR:500
  };

  class StyleOmgQuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.sectionId = this.dataset.section;
      this.product = this.closest('product-info') ||
        document.getElementById('MainProduct-' + this.sectionId);
      this.form = document.getElementById('product-form-' + this.sectionId);
      this.key = this.sectionId + ':' + (this.product?.dataset.productId || 'product');
      this.previous = remembered.get(this.key) || null;
      this.radios = [...this.querySelectorAll('.so-bundle__radio')];
      this.cards = this.radios.map(radio =>
        this.querySelector('label[for="' + radio.id + '"]')
      );
      this.bar = null;
      this.status = null;
      this.findShipping();
      this.savings = this.querySelector('[data-so-savings-alert]');
      this.savingsAmount = this.querySelector('[data-so-total-saving]');
      this.currency = this.dataset.currency || 'USD';
      this.shopCurrency = this.dataset.shopCurrency || 'USD';
      this.locale = this.dataset.locale || 'en-US';
      this.baseUSD = Math.max(0, Number(this.dataset.shippingBaseUsd ?? 80));
      this.shipEnabled = this.dataset.showShipping === 'true';
      this.cartTotal = Math.max(0, Number(this.dataset.cartTotal || 0));
      this.cartItems = 0;
      this.cartCurrencyUnknown = false;
      this.threshold = this.currency === this.shopCurrency
        ? Math.round(this.baseUSD * 100) : null;
      this.approximate = this.currency !== this.shopCurrency;
      this._pendingPercent = null;
      this._restoreScheduled = false;
      this._seenVariantId = '';
      this.variant = this.parseJson(this.querySelector('[data-so-variants]')) || {};
      this.localVariants = this.parseJson(this.product?.querySelector('[data-so-fast-variants]')) || [];

      const hiddenQty = this.form?.querySelector('[data-styleomg-qty-input]');
      if (hiddenQty) hiddenQty.disabled = true;
      const fallbackUi = this.form?.querySelector('[data-styleomg-qty]');
      if (fallbackUi) fallbackUi.style.setProperty('display','none','important');

      const chosenQty = this.previous?.quantity;
      if (chosenQty != null) {
        const chosen = this.radios.find(radio => Number(radio.value) === chosenQty);
        if (chosen) {
          this.radios.forEach(radio => { radio.checked = radio === chosen; });
        }
      }
      this.dataset.selectedQuantity = this.getSelectedRadio()?.value || '1';

      if (this.bar && Number.isFinite(this.previous?.percent)) {
        // When Shopify replaces the product section, restart from its last
        // visible progress rather than jumping back to the initial Liquid value.
        this.bar.style.transition = 'none';
        this.bar.style.transform = 'scaleX(' + (Math.max(0, Math.min(100, this.previous.percent)) / 100) + ')';
        void this.bar.offsetWidth;
        this._restoreScheduled = true;
      }

      this._onQuantityChange = event => {
        if (!event.target.matches?.('.so-bundle__radio')) return;
        this.dataset.selectedQuantity = event.target.value;
        this.remember();
        this.render();
      };
      this.addEventListener('change', this._onQuantityChange);

      this._onVariantChange = event => {
        if (!event.target.closest?.('variant-selects') &&
            !event.target.matches?.('input[name="id"]')) return;
        queueMicrotask(() => { if (this.isConnected) this.render(); });
      };
      this.product?.addEventListener('change', this._onVariantChange, true);
      this._onCart = () => this.refreshCart();
      ['cart:updated','cart:refresh','cart:change','cart-drawer:updated'].forEach(evt =>
        document.addEventListener(evt, this._onCart)
      );
      // Shopify publishes this event only after a cart mutation succeeds.
      // Updating the selected bundle quantity alone never mutates the cart.
      if (typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (this.isConnected) this.refreshCart();
        });
      }
      this._onPageShow = () => this.refreshCart();
      window.addEventListener('pageshow', this._onPageShow);
      this._onVisible = () => { if (!document.hidden) this.refreshCart(); };
      document.addEventListener('visibilitychange', this._onVisible);
      this._onFormSubmit = () => {
        window.setTimeout(() => { if (this.isConnected) this.refreshCart(); }, 900);
      };
      this.form?.addEventListener('submit', this._onFormSubmit);

      if (typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
          if (!this.isConnected || data.sectionId !== this.sectionId) return;
          if (data.variant) this.variant = data.variant;
          this.render();
        });
      }
      this.render();
      if (this._restoreScheduled) {
        this._raf = requestAnimationFrame(() => {
          if (!this.isConnected || !this.bar) return;
          // First paint reinstates the CSS transition at the remembered
          // position; the following frame animates to the next target.
          this.bar.style.removeProperty('transition');
          void this.bar.offsetWidth;
          this._restoreScheduled = false;
          this._raf = requestAnimationFrame(() => {
            if (this.isConnected && this._pendingPercent != null) {
              this.applyProgress(this._pendingPercent);
            }
          });
        });
      }
      this.resolveCurrency();
      this.refreshCart();
      this._seenVariantId = String(this.getVariantId() || '');
      // Watch only the hidden variant ID, not the entire DOM or animation.
      this._variantWatch = window.setInterval(() => {
        if (!this.isConnected) return;
        const id = String(this.getVariantId() || '');
        if (id !== this._seenVariantId) {
          this._seenVariantId = id;
          this.render();
        }
      }, 280);
    }

    disconnectedCallback() {
      if (!this._connected) return;
      this.remember(true);
      this._connected = false;
      this.removeEventListener('change', this._onQuantityChange);
      this.product?.removeEventListener('change', this._onVariantChange, true);
      ['cart:updated','cart:refresh','cart:change','cart-drawer:updated'].forEach(evt =>
        document.removeEventListener(evt, this._onCart)
      );
      window.removeEventListener('pageshow', this._onPageShow);
      document.removeEventListener('visibilitychange', this._onVisible);
      this.form?.removeEventListener('submit', this._onFormSubmit);
      this.unsubscribeVariant?.();
      this.unsubscribeCart?.();
      this._cartController?.abort();
      window.clearInterval(this._variantWatch);
      window.clearTimeout(this._currencyTimer);
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    parseJson(node) {
      try { return JSON.parse(node?.textContent || 'null'); } catch (_) { return null; }
    }

    findShipping() {
      const widget = document.getElementById('SoPDPShipping-' + this.sectionId);
      if (!widget) return;
      this.shippingWidget = widget;
      this.bar = widget.querySelector('[data-so-shipping-fill]');
      this.status = widget.querySelector('[data-so-shipping-summary]');
      const thresholdInput = Number(widget.dataset.shippingUsd);
      if (Number.isFinite(thresholdInput) && thresholdInput > 0) this.baseUSD = thresholdInput;
      this.shipEnabled = widget.dataset.shippingEnabled === 'true';
    }

    getSelectedRadio() { return this.radios.find(r => r.checked) || this.radios[0]; }
    getVariantId() {
      const form = document.getElementById('product-form-' + this.sectionId);
      if (form && form !== this.form) this.form = form;
      return this.form?.querySelector('input.product-variant-id[name="id"], input[name="id"]')?.value;
    }

    currentVariant() {
      const id = String(this.getVariantId() || '');
      const found = Array.isArray(this.localVariants)
        ? this.localVariants.find(v => String(v.id) === id) : null;
      if (found) return found;
      if (id && String(this.variant?.id || '') === id) return this.variant;
      // The quantity block renders ahead of the Add to Cart form. On an
      // asynchronously inserted section the form may not be connected yet.
      // Use the server-rendered selected variant until its ID is available.
      if (!id && Number.isFinite(Number(this.variant?.price))) return this.variant;
      return null;
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style:'currency', currency:this.currency, currencyDisplay:'symbol'
        }).format(amount);
      } catch (_) { return this.currency + ' ' + amount.toFixed(2); }
    }

    remember(visual = false) {
      const item = remembered.get(this.key) || {};
      const qty = Number(this.getSelectedRadio()?.value);
      if (Number.isFinite(qty) && qty > 0) item.quantity = qty;
      if (visual && this.bar) {
        const track = this.bar.parentElement;
        const width = track?.getBoundingClientRect().width || 0;
        const rendered = this.bar.getBoundingClientRect().width;
        if (width > 0 && Number.isFinite(rendered))
          item.percent = Math.max(0, Math.min(100, rendered / width * 100));
      } else if (Number.isFinite(this._pendingPercent)) {
        item.percent = this._pendingPercent;
      }
      remembered.set(this.key, item);
      if (remembered.size > 20) remembered.delete(remembered.keys().next().value);
    }

    applyProgress(value) {
      this._pendingPercent = Math.max(0, Math.min(100, value));
      if (this._restoreScheduled || !this.bar) return;
      const target = 'scaleX(' + (this._pendingPercent / 100).toFixed(4) + ')';
      if (this.bar.style.transform !== target) this.bar.style.transform = target;
      this.remember();
    }

    render() {
      const variant = this.currentVariant();
      // Cart eligibility must update even while Shopify changes variants.
      if (!variant || !Number.isFinite(Number(variant.price))) {
        this.renderShipping();
        return;
      }
      const unit = Number(variant.price);
      const compareAt = Math.max(unit, Number(variant.compare_at_price || 0));
      const chosen = this.getSelectedRadio();
      let selectedTotal = 0;
      let selectedQty = 1;

      this.radios.forEach((radio, i) => {
        const card = this.cards[i];
        if (!card) return;
        const qty = Math.max(1, Number(radio.value || 1));
        const discount = Math.max(0, Math.min(100, Number(radio.dataset.soDiscount || 0)));
        const gross = unit * qty;
        const discounted = gross - Math.round(gross * discount / 100);
        const price = card.querySelector('[data-so-sale-qty]');
        // Buy 1 shows its original compare-at price (when on sale).
        // Packs show the undiscounted CURRENT item price × quantity,
        // so “You save” means the extra bundle discount only.
        const regular = qty === 1
          ? Math.max(unit,Number(variant.compare_at_price || 0))
          : unit * qty;
        const saved = qty === 1 ? 0 : Math.max(0,regular-discounted);
        const old = card.querySelector('[data-so-compare-qty]');
        const saveRow = card.querySelector('[data-so-save-row]');
        const saveValue = card.querySelector('[data-so-save-qty]');
        if (old) {
          old.hidden = regular <= discounted;
          if (!old.hidden) old.textContent = this.money(regular);
        }
        if (saveRow) saveRow.hidden = qty === 1 || saved < 1;
        if (saveValue && saved > 0) saveValue.textContent = this.money(saved);
        if (price) {
          const formatted = this.money(discounted);
          if (price.textContent !== formatted) price.textContent = formatted;
          price.classList.toggle('so-long-price', formatted.length > 10);
        }
        if (radio === chosen) {
          selectedTotal = discounted;
          selectedQty = qty;
        }
      });
      if (this.savings && this.savingsAmount) {
        const saved = Math.max(0, compareAt * selectedQty - selectedTotal);
        this.savings.hidden = saved < 1;
        if (saved > 0) this.savingsAmount.textContent = this.money(saved);
      }
      this.renderShipping();
    }

    renderShipping() {
      // This section lives AFTER the payment buttons in the document.
      // Shopify may replace the product section during variant changes.
      this.findShipping();
      if (!this.shipEnabled) return;
      if (this.cartCurrencyUnknown) {
        this.applyProgress(0);
        if (this.status) this.status.textContent = 'Shipping calculated at checkout';
        return;
      }

      // Free-shipping eligibility is based ONLY on the existing Shopify cart.
      // Selected bundle tiers are previews; they are not cart line items.
      const actualCartTotal = Math.max(0, this.cartTotal);
      if (this.threshold === null || this.threshold <= 0) {
        this.applyProgress(0);
        if (this.status) this.status.textContent = 'Shipping calculated at checkout';
      } else {
        this.applyProgress(actualCartTotal / this.threshold * 100);
        const missing = Math.max(0, this.threshold - actualCartTotal);
        if (this.status) {
          let message;
          if (missing < 1) message = this.dataset.shipUnlocked || 'Free shipping unlocked!';
          else message = 'Spend ' + this.money(missing) + ' more for ' +
            (this.dataset.freeCopy || 'FREE shipping');
          if (this.approximate) message = 'Est. ' + message;
          if (this.status.textContent !== message) this.status.textContent = message;
        }
      }
    }

    async refreshCart() {
      if (!this.isConnected) return;
      this._cartController?.abort();
      const controller = new AbortController();
      this._cartController = controller;
      try {
        const route = window.Shopify?.routes?.root || '/';
        const url = route.endsWith('/') ? route + 'cart.js' : route + '/cart.js';
        const response = await fetch(url, {
          credentials:'same-origin', headers:{Accept:'application/json'},
          cache:'no-store', signal:controller.signal
        });
        if (!response.ok || controller !== this._cartController || !this.isConnected) return;
        const cart = await response.json();
        this.cartTotal = Math.max(0, Number(cart.total_price || 0));
        this.cartItems = Math.max(0, Number(cart.item_count || 0));
        this.cartCurrencyUnknown = false;
        if (cart.currency && cart.currency !== this.currency) {
          // Most Shopify Ajax carts already use presentment money. If not, do
          // not claim an exact local amount from mismatched currencies.
          const rate = Number(window.Shopify?.currency?.rate);
          if (this.shopCurrency === cart.currency && Number.isFinite(rate) && rate > 0) {
            this.cartTotal = Math.round(this.cartTotal * rate);
          } else {
            this.cartCurrencyUnknown = true;
          }
          this.approximate = true;
        }
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('StyleOmg cart progress unavailable', error);
      }
    }

    resolveCurrency() {
      if (!this.shipEnabled) return;
      if (this.currency === this.shopCurrency) {
        this.threshold = Math.round(this.baseUSD * 100);
        this.approximate = false;
        this.render();
        return;
      }
      const tryRate = (attempt) => {
        if (!this.isConnected) return;
        const rate = Number(window.Shopify?.currency?.rate);
        if (this.shopCurrency === 'USD' && Number.isFinite(rate) && rate > 0) {
          this.threshold = Math.ceil(this.baseUSD * rate * 100);
          this.approximate = true;
          this.render();
          return;
        }
        if (attempt < 12) {
          this._currencyTimer = window.setTimeout(() => tryRate(attempt + 1), 160);
          return;
        }
        const fallback = Number(fallback120[this.currency]);
        if (this.shopCurrency === 'USD' && Number.isFinite(fallback) && fallback > 0) {
          this.threshold = Math.round(fallback * this.baseUSD / 120 * 100);
          this.approximate = true;
        }
        this.render();
      };
      tryRate(0);
    }
  }

  customElements.define('styleomg-quantity-breaks', StyleOmgQuantityBreaks);
})();